import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Copy,
  Check,
  RefreshCw,
  Search,
  Loader2,
  AlertOctagon,
  CircleDollarSign,
  ExternalLink,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import {
  ERROR_COSTS,
  SOURCE_BADGE_VARIANTS,
  SOURCE_LABELS,
  type ErrorSource,
} from "@/lib/errorCosts";
import { RefundDialog } from "./RefundDialog";
import { cn } from "@/lib/utils";

interface UnifiedErrorRow {
  source: ErrorSource;
  source_id: string;
  created_at: string;
  user_id: string | null;
  user_email: string | null;
  title: string;
  endpoint: string;
  error_message: string;
  status: string | null;
  context: Record<string, unknown>;
  raw: Record<string, unknown>;
}

interface RefundRecord {
  source: string;
  source_id: string;
  credits_refunded: number;
  created_at: string;
  is_auto?: boolean;
}

type FilterKey =
  | "all"
  | ErrorSource
  | "open"
  | "last24h"
  | "suspicious";

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "content_filter", label: "Content Filter" },
  { key: "graph", label: "Graph API" },
  { key: "report", label: "User Reports" },
  { key: "extraction", label: "Extraction Fails" },
  { key: "smart_note", label: "Smart Note Fails" },
  { key: "open", label: "Open Reports" },
  { key: "last24h", label: "Last 24h" },
  { key: "suspicious", label: "Suspicious Activity" },
];

interface FlaggedUser {
  user_id: string;
  paused: boolean;
  flagged: boolean;
}

export function UnifiedErrorsLog() {
  const [rows, setRows] = useState<UnifiedErrorRow[]>([]);
  const [refunds, setRefunds] = useState<Map<string, RefundRecord>>(new Map());
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [search, setSearch] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [refundTarget, setRefundTarget] = useState<UnifiedErrorRow | null>(null);
  const [flaggedUsers, setFlaggedUsers] = useState<Map<string, FlaggedUser>>(new Map());
  const [refundHealth, setRefundHealth] = useState<{
    total: number;
    refunded: number;
    no_deduction: number;
    capped: number;
    blocked: number;
    pending: number;
  } | null>(null);

  const refundKey = (source: string, source_id: string) => `${source}|${source_id}`;

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [
        { data: contentFilterData },
        { data: graphData },
        { data: reportData },
        { data: extractionData },
        { data: smartNoteData },
        { data: refundData },
        { data: flaggedData },
      ] = await Promise.all([
        supabase
          .from("signature_extraction_logs")
          .select("id, created_at, user_id, user_email, sender_email, error_message, is_test_mail")
          .ilike("error_message", "CONTENT_FILTER%")
          .not("error_message", "ilike", "CONTENT_FILTER_FALLBACK%")
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("graph_api_error_logs")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("error_reports")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("signature_extraction_logs")
          .select("id, created_at, user_id, user_email, sender_email, error_message")
          .eq("extraction_success", false)
          .not("error_message", "ilike", "CONTENT_FILTER%")
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("smart_notes_logs")
          .select("id, created_at, user_id, company_name, error_message, success")
          .eq("success", false)
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("error_refunds")
          .select("source, source_id, credits_refunded, created_at"),
        supabase
          .from("subscriptions")
          .select("user_id, auto_refund_review_required, auto_refund_paused_by_admin")
          .or("auto_refund_review_required.eq.true,auto_refund_paused_by_admin.eq.true"),
      ]);

      // Refund-pipeline health (last 7 days)
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: healthData } = await supabase
        .from("error_reports")
        .select("status")
        .gte("created_at", sevenDaysAgo);
      if (healthData) {
        const health = {
          total: healthData.length,
          refunded: 0,
          no_deduction: 0,
          capped: 0,
          blocked: 0,
          pending: 0,
        };
        for (const row of healthData) {
          if (row.status === "refunded") health.refunded++;
          else if (row.status === "no_deduction_found") health.no_deduction++;
          else if (row.status === "refund_capped") health.capped++;
          else if (row.status === "refund_blocked") health.blocked++;
          else health.pending++;
        }
        setRefundHealth(health);
      }

      const merged: UnifiedErrorRow[] = [];

      // Build userId -> email map from sources that already carry user_email,
      // so Graph API and Smart Notes rows (user_id only) can show the user too.
      const emailByUserId = new Map<string, string>();
      const collectEmail = (uid: string | null | undefined, email: string | null | undefined) => {
        if (uid && email && !emailByUserId.has(uid)) emailByUserId.set(uid, email);
      };
      (contentFilterData ?? []).forEach((r: any) => collectEmail(r.user_id, r.user_email));
      (extractionData ?? []).forEach((r: any) => collectEmail(r.user_id, r.user_email));
      (reportData ?? []).forEach((r: any) => collectEmail(r.user_id, r.user_email));

      // Identify Graph + Smart Notes user_ids without an email yet
      const missingUserIds = new Set<string>();
      (graphData ?? []).forEach((r: any) => {
        if (r.user_id && !emailByUserId.has(r.user_id)) missingUserIds.add(r.user_id);
      });
      (smartNoteData ?? []).forEach((r: any) => {
        if (r.user_id && !emailByUserId.has(r.user_id)) missingUserIds.add(r.user_id);
      });

      // Backfill from signature_extraction_logs (covers active users who do extractions)
      if (missingUserIds.size > 0) {
        const { data: backfill } = await supabase
          .from("signature_extraction_logs")
          .select("user_id, user_email")
          .in("user_id", Array.from(missingUserIds))
          .not("user_email", "is", null)
          .limit(1000);
        (backfill ?? []).forEach((r: any) => collectEmail(r.user_id, r.user_email));

        // Final fallback for user_ids still without an email: admin-only RPC
        // that resolves emails directly from auth.users.
        const stillMissing = Array.from(missingUserIds).filter((id) => !emailByUserId.has(id));
        if (stillMissing.length > 0) {
          const { data: rpcEmails, error: rpcErr } = await supabase.rpc(
            "admin_get_user_emails",
            { _user_ids: stillMissing }
          );
          if (rpcErr) {
            console.warn("[UnifiedErrorsLog] admin_get_user_emails failed", rpcErr);
          } else {
            (rpcEmails ?? []).forEach((r: any) => collectEmail(r.user_id, r.email));
          }
        }
      }

      const resolveEmail = (uid: string | null) => (uid ? emailByUserId.get(uid) ?? null : null);

      (contentFilterData ?? []).forEach((r: any) =>
        merged.push({
          source: "content_filter",
          source_id: r.id,
          created_at: r.created_at,
          user_id: r.user_id,
          user_email: r.user_email ?? resolveEmail(r.user_id),
          title: r.sender_email ? `From: ${r.sender_email}` : "Content Filter",
          endpoint: "Azure OpenAI / Vision",
          error_message: r.error_message ?? "",
          status: r.is_test_mail ? "test" : null,
          context: { is_test_mail: r.is_test_mail },
          raw: r,
        })
      );

      (graphData ?? []).forEach((r: any) =>
        merged.push({
          source: "graph",
          source_id: r.id,
          created_at: r.created_at,
          user_id: r.user_id,
          user_email: resolveEmail(r.user_id),
          title: r.error_code ? `[${r.error_code}]` : `${r.method} error`,
          endpoint: `${r.method} ${r.endpoint}`,
          error_message: r.error_message ?? "",
          status: r.status ? String(r.status) : null,
          context: { request_body_summary: r.request_body_summary },
          raw: r,
        })
      );

      (reportData ?? []).forEach((r: any) =>
        merged.push({
          source: "report",
          source_id: r.id,
          created_at: r.created_at,
          user_id: r.user_id,
          user_email: r.user_email ?? resolveEmail(r.user_id),
          title: r.error_title,
          endpoint: (r.error_context as any)?.route || "—",
          error_message: r.error_message ?? "",
          status: r.status,
          context: (r.error_context as Record<string, unknown>) || {},
          raw: r,
        })
      );

      (extractionData ?? []).forEach((r: any) =>
        merged.push({
          source: "extraction",
          source_id: r.id,
          created_at: r.created_at,
          user_id: r.user_id,
          user_email: r.user_email ?? resolveEmail(r.user_id),
          title: r.sender_email ? `From: ${r.sender_email}` : "Extraction failed",
          endpoint: "Signature extraction",
          error_message: r.error_message ?? "",
          status: null,
          context: {},
          raw: r,
        })
      );

      (smartNoteData ?? []).forEach((r: any) =>
        merged.push({
          source: "smart_note",
          source_id: r.id,
          created_at: r.created_at,
          user_id: r.user_id,
          user_email: resolveEmail(r.user_id),
          title: r.company_name ? `Company: ${r.company_name}` : "Smart Note failed",
          endpoint: "Smart Notes research",
          error_message: r.error_message ?? "",
          status: null,
          context: {},
          raw: r,
        })
      );

      merged.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      const refundMap = new Map<string, RefundRecord>();
      (refundData ?? []).forEach((r: any) => {
        if (r.source === "auto") {
          // Auto-refunds always reference an error_reports row.
          // Map them under both the canonical "report" key and the literal "auto" key
          // so the badge/disabled-button logic finds them no matter which source the row uses.
          const rec: RefundRecord = { ...r, is_auto: true };
          refundMap.set(refundKey("report", r.source_id), rec);
          refundMap.set(refundKey("auto", r.source_id), rec);
        } else {
          refundMap.set(refundKey(r.source, r.source_id), r);
        }
      });

      const flagMap = new Map<string, FlaggedUser>();
      (flaggedData ?? []).forEach((s: any) => {
        flagMap.set(s.user_id, {
          user_id: s.user_id,
          paused: !!s.auto_refund_paused_by_admin,
          flagged: !!s.auto_refund_review_required,
        });
      });

      setRows(merged);
      setRefunds(refundMap);
      setFlaggedUsers(flagMap);
    } catch (err: any) {
      console.error("[UnifiedErrorsLog] fetch failed", err);
      toast({ title: "Failed to load errors", description: err?.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = useMemo(() => {
    const cutoff24h = Date.now() - 24 * 60 * 60 * 1000;
    return rows.filter((r) => {
      if (filter === "open") {
        if (r.source !== "report" || r.status === "resolved" || r.status === "refunded") return false;
      } else if (filter === "last24h") {
        if (new Date(r.created_at).getTime() < cutoff24h) return false;
      } else if (filter === "suspicious") {
        if (!r.user_id || !flaggedUsers.has(r.user_id)) return false;
      } else if (filter !== "all" && r.source !== filter) {
        return false;
      }

      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const haystack = [
          r.user_email,
          r.user_id,
          r.error_message,
          r.endpoint,
          r.title,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filter, search, flaggedUsers]);

  const togglePauseUser = async (userId: string, currentPaused: boolean) => {
    const { error } = await supabase
      .from("subscriptions")
      .update({
        auto_refund_paused_by_admin: !currentPaused,
        // Clearing the pause also clears the review flag so the user gets a clean slate.
        ...(currentPaused ? { auto_refund_review_required: false } : {}),
      })
      .eq("user_id", userId);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({
      title: currentPaused ? "Auto-refunds resumed" : "Auto-refunds paused",
      description: currentPaused
        ? "User will receive auto-refunds again."
        : "User will no longer receive automatic refunds.",
    });
    fetchAll();
  };

  const clearReviewFlag = async (userId: string) => {
    const { error } = await supabase
      .from("subscriptions")
      .update({ auto_refund_review_required: false })
      .eq("user_id", userId);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Review flag cleared" });
    fetchAll();
  };

  const buildCopyBlock = (r: UnifiedErrorRow): string => {
    const refund = refunds.get(refundKey(r.source, r.source_id));
    const lines = [
      `Date: ${format(new Date(r.created_at), "yyyy-MM-dd HH:mm:ss")}`,
      `Source: ${SOURCE_LABELS[r.source]}`,
      `Title: ${r.title}`,
      `User: ${r.user_email || "(unknown)"}`,
      `User ID: ${r.user_id || "(unknown)"}`,
      `Endpoint: ${r.endpoint}`,
      `Status: ${r.status ?? "—"}`,
      `Error: ${r.error_message}`,
      `Source ID: ${r.source_id}`,
      `Context: ${JSON.stringify(r.context, null, 2)}`,
    ];
    if (refund) {
      lines.push(`Refunded: ${refund.credits_refunded} credits at ${refund.created_at}`);
    }
    return lines.join("\n");
  };

  const handleCopy = async (r: UnifiedErrorRow) => {
    const id = `${r.source}|${r.source_id}`;
    try {
      await navigator.clipboard.writeText(buildCopyBlock(r));
      setCopiedId(id);
      setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1800);
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  return (
    <Card className="app-tile app-tile-hover">
      <CardHeader className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="app-icon-box">
              <AlertOctagon className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <CardTitle className="app-section-title flex items-center gap-2">
                All Errors
                <Badge variant="outline" className="font-mono tabular-nums">
                  {filtered.length} / {rows.length}
                </Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-0.5">
                Unified view across content filter, Graph API, extraction and user reports.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-2">Refresh</span>
          </Button>
        </div>

        {refundHealth && refundHealth.total > 0 && (
          <div className="rounded-lg border bg-muted/30 px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              <span className="font-medium text-foreground">
                Auto-refund pipeline (last 7 days):
              </span>
              <span className="text-muted-foreground">
                <span className="font-mono font-semibold text-foreground">{refundHealth.total}</span> errors
              </span>
              <span className="text-muted-foreground">→</span>
              <span className="text-success">
                <span className="font-mono font-semibold">{refundHealth.refunded}</span> refunded
              </span>
              {refundHealth.no_deduction > 0 && (
                <span className="text-warning">
                  <span className="font-mono font-semibold">{refundHealth.no_deduction}</span> no deduction
                </span>
              )}
              {refundHealth.capped > 0 && (
                <span className="text-warning">
                  <span className="font-mono font-semibold">{refundHealth.capped}</span> capped
                </span>
              )}
              {refundHealth.blocked > 0 && (
                <span className="text-destructive">
                  <span className="font-mono font-semibold">{refundHealth.blocked}</span> blocked
                </span>
              )}
              {refundHealth.pending > 0 && (
                <span className="text-muted-foreground">
                  <span className="font-mono font-semibold">{refundHealth.pending}</span> pending
                </span>
              )}
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <Button
              key={f.key}
              variant={filter === f.key ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f.key)}
              className="h-8 rounded-full px-3 text-xs"
            >
              {f.label}
              {f.key === "suspicious" && flaggedUsers.size > 0 && (
                <Badge variant="destructive" className="ml-2 h-4 px-1.5 text-[10px]">
                  {flaggedUsers.size}
                </Badge>
              )}
            </Button>
          ))}
        </div>

        {flaggedUsers.size > 0 && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3">
            <div className="flex items-center gap-2 text-sm font-medium text-destructive">
              <AlertOctagon className="h-4 w-4" />
              {flaggedUsers.size} user{flaggedUsers.size === 1 ? "" : "s"} flagged for refund review
            </div>
            <div className="mt-2 space-y-1.5">
              {Array.from(flaggedUsers.values()).map((u) => (
                <div
                  key={u.user_id}
                  className="flex flex-wrap items-center justify-between gap-2 text-xs"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono">{u.user_id}</span>
                    {u.flagged && (
                      <Badge variant="destructive" className="text-[10px]">
                        Review required
                      </Badge>
                    )}
                    {u.paused && (
                      <Badge variant="outline" className="text-[10px]">
                        Auto-refunds paused
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-1.5">
                    {u.flagged && !u.paused && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => clearReviewFlag(u.user_id)}
                      >
                        Clear flag
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant={u.paused ? "default" : "destructive"}
                      className="h-7 text-xs"
                      onClick={() => togglePauseUser(u.user_id, u.paused)}
                    >
                      {u.paused ? "Resume auto-refunds" : "Pause auto-refunds"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search email, user ID, error message, endpoint..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="py-12 text-center text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
            Loading errors…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">No errors match the current filter.</div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Date</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Title / Endpoint</TableHead>
                  <TableHead>Error</TableHead>
                  <TableHead className="w-[110px]">Status</TableHead>
                  <TableHead className="text-right w-[180px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => {
                  const id = `${r.source}|${r.source_id}`;
                  const refund = refunds.get(refundKey(r.source, r.source_id));
                  const isExpanded = expandedId === id;
                  return (
                    <TableRow key={id} className={cn(refund && "bg-muted/40")}>
                      <TableCell className="font-mono text-xs whitespace-nowrap">
                        {format(new Date(r.created_at), "dd.MM.yy HH:mm:ss")}
                      </TableCell>
                      <TableCell>
                        <Badge variant={SOURCE_BADGE_VARIANTS[r.source]} className="text-xs">
                          {SOURCE_LABELS[r.source]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs max-w-[200px]">
                        <div className="font-medium truncate" title={r.user_email || r.user_id || undefined}>
                          {r.user_email || "—"}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs max-w-[220px]">
                        <div className="font-medium truncate">{r.title}</div>
                        <div className="text-muted-foreground font-mono text-[10px] truncate">{r.endpoint}</div>
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-xs select-all cursor-pointer max-w-[320px]",
                          !isExpanded && "truncate"
                        )}
                        onClick={() => setExpandedId(isExpanded ? null : id)}
                        title="Click to expand"
                      >
                        {r.error_message}
                      </TableCell>
                      <TableCell>
                        {refund ? (
                          <Badge
                            variant={refund.is_auto ? "secondary" : "default"}
                            title={refund.is_auto ? "Refunded automatically by the system" : undefined}
                          >
                            {refund.is_auto ? "Auto-refunded" : "Refunded"} {refund.credits_refunded}
                          </Badge>
                        ) : r.status === "refund_capped" ? (
                          <Badge variant="outline" className="text-xs" title="Refund skipped — user hit hourly/daily cap">
                            Refund capped
                          </Badge>
                        ) : r.status === "refund_blocked" ? (
                          <Badge variant="destructive" className="text-xs" title="Refund blocked by review flag or admin pause">
                            Refund blocked
                          </Badge>
                        ) : r.status === "no_deduction_found" ? (
                          <Badge variant="outline" className="text-xs" title="No matching credit deduction in last 5 minutes — refund skipped">
                            No deduction
                          </Badge>
                        ) : r.status ? (
                          <Badge variant="outline" className="text-xs">
                            {r.status}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            title="Copy row"
                            onClick={() => handleCopy(r)}
                          >
                            {copiedId === id ? (
                              <Check className="h-4 w-4 text-primary" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            title={
                              refund
                                ? refund.is_auto
                                  ? `Already auto-refunded (${refund.credits_refunded} credits)`
                                  : `Already refunded (${refund.credits_refunded} credits)`
                                : "Refund credits + 5 bonus"
                            }
                            disabled={!r.user_id || !!refund}
                            onClick={() => setRefundTarget(r)}
                          >
                            <CircleDollarSign className="h-4 w-4" />
                          </Button>
                          {r.user_id && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              title="Open user (copy ID)"
                              onClick={() => {
                                navigator.clipboard.writeText(r.user_id!);
                                toast({ title: "User ID copied" });
                              }}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {refundTarget && refundTarget.user_id && (
        <RefundDialog
          open={!!refundTarget}
          onOpenChange={(open) => !open && setRefundTarget(null)}
          source={refundTarget.source}
          sourceId={refundTarget.source_id}
          targetUserId={refundTarget.user_id}
          targetUserEmail={refundTarget.user_email}
          errorSummary={refundTarget.error_message}
          onSuccess={fetchAll}
        />
      )}
    </Card>
  );
}
